import { Delete, Edit, Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import { Contact, ContactType, useAddMemberMutation } from "@repo/server-api";
import { EmailInformation, RuleFactoryType } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { emptyContact } from "@repo/legacy/components/helper/Index";
import { useAllMembersDataSource } from "@repo/legacy/components/shared/datasources/AllMembersDataSource";
import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";
import List from "@repo/legacy/components/shared/lists/List";

import DialogCUEmployee from "../dialogs/DialogCUEmployee";
import { DataSource } from "../lists/DataSource";

interface ActionCellProps {
	params: GridCellParams;
	allMembers: DataSource<Contact>;
	props: { setContact: (contact: Contact | null) => void; contactType: ContactType };
	contact: Contact | null;
	openDelete: (id: string) => void;
	closeDelete: () => void;
}

const ActionCell: React.FC<ActionCellProps> = ({ params, props, openDelete, closeDelete }) => {
	const navigate = useNavigate();

	const openEdit = (id: string) => {
		props.setContact(null);
		const target = props.contactType === ContactType.Employee ? "employees" : "freelancers";
		closeDelete();
		navigate(`/${target}/${encodeURIComponent(id)}`);
	};

	return (
		<div>
			<Button
				data-testid="edit-button"
				onClick={() => {
					openEdit(params.id.toString());
				}}>
				<Edit />
			</Button>
			<Button data-testid="delete-button" onClick={() => openDelete(params.id.toString())}>
				<Delete />
			</Button>
		</div>
	);
};

export default function CompanyEmployees(
	props: Readonly<{
		contact: Contact | null;
		contactType: ContactType;
		setContact: (contact: Contact | null) => void;
		ruleFactory: RuleFactoryType;
	}>,
) {
	const contact = useMemo(() => props.contact, [props.contact]);
	const [openDialog, setOpenDialog] = useState<boolean>(false);

	const { showNotification } = useNotification();

	const [deleteId, setDeleteId] = useState<string | null>(null);
	const openDelete = (id: string) => setDeleteId(id);
	const closeDelete = () => setDeleteId(null);

	const handleConfirmDelete = () => {
		if (!deleteId) return;
		allMembers.deleteDataMember?.(deleteId, contact?.id ?? "");
		closeDelete();
	};

	const allMembers = useAllMembersDataSource();

	const [addMember] = useAddMemberMutation({
		onCompleted(data) {
			if (data.addMember.boolean) {
				allMembers.refresh?.();
				showNotification(t("member.created"), "success");
			}
		},
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
	});

	const { t } = useTranslation();

	const columns: GridColDef<Contact>[] = useMemo(
		() => [
			{
				field: "displayName",
				headerName: t("name"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.displayName;
				},
			},
			{
				field: "additionalInformation",
				headerName: t("email"),
				width: 300,
				renderCell: (params: GridCellParams) => {
					return params.row.additionalInformation
						.filter(
							(e: { __typename?: string }): e is EmailInformation =>
								e.__typename === "EmailInformation",
						)
						.map((e: { emailAddress: string }) => e.emailAddress)
						.join(", ");
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params: GridCellParams) => {
					return (
						<ActionCell
							params={params}
							allMembers={allMembers}
							props={props}
							contact={contact}
							openDelete={openDelete}
							closeDelete={closeDelete}
						/>
					);
				},
			},
		],

		[t, allMembers, props, contact],
	);

	const handleMember = (member: Contact) => {
		const all = allMembers.rows.map((c) => c);
		const x = all.find((c) => c.id === member.id);

		if (!x) {
			if (!contact?.contactTypes.includes(ContactType.Contractor))
				addMember({
					variables: { memberId: member.id, organizationId: contact?.id ?? "" },
				});
			else {
				allMembers.refresh?.();
				showNotification(t("members.created"), "success");
			}
		} else {
			// Reload members
			if (contact && contact.id) {
				allMembers.refresh?.();
				showNotification(t("members.updated"), "success");
			}
		}
	};

	return (
		<Box sx={{ width: "100%" }}>
			<DialogCUEmployee
				contact={{
					...emptyContact,
					contactTypes: [props.contactType],
				}}
				contactType={props.contactType}
				refreshData={handleMember}
				setOpen={setOpenDialog}
				open={openDialog}
				contractorId={contact?.id}
				ruleFactory={props.ruleFactory}
			/>
			<ConformationDialog
				text={t("contact.deleteConformationMember")}
				setOpenDeleteConformation={(open) => {
					if (!open) closeDelete();
				}}
				openPopup={Boolean(deleteId)}
				handleYes={handleConfirmDelete}
			/>
			<List
				columns={columns as GridCellParams[]}
				dataSource={allMembers}
				members={true}
				contactId={contact?.id}>
				<Box className="clear">
					<Button
						variant="contained"
						className="greenButton"
						onClick={() => {
							setOpenDialog(true);
						}}>
						{props.contactType === ContactType.Employee
							? t("create-new-employee")
							: t("freelancer.create")}{" "}
						<AddIcon />
					</Button>
				</Box>
			</List>
		</Box>
	);
}
